/**
 * KPI tile. Every tile carries a definition — the /summary payload supplies one
 * per tile, and buildDefLookup fills the gap from /metric-definitions (or the
 * local fallback) when it does not.
 */
import React, { type ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Def, type DefLookup } from './apiContract';

interface MetricTileProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  defKey: string;
  def: DefLookup;
  /** Server-supplied definition text; wins over the lookup when present. */
  definition?: string | null;
  faded?: boolean;
  size?: 'lg' | 'sm';
}

export const MetricTile: React.FC<MetricTileProps> = ({
  label,
  value,
  sub,
  defKey,
  def,
  definition,
  faded,
  size = 'lg',
}) => (
  <Card className={cn(faded && 'opacity-60')}>
    <CardContent className="p-3">
      <div className="flex items-start justify-between gap-1">
        <div className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-gray-500">
          {label}
        </div>
        <Def def={def} k={defKey} text={definition ?? undefined} className="mt-[1px] shrink-0" />
      </div>
      <div
        className={cn(
          'mt-1 font-bold leading-none tabular-nums text-heritage-blue',
          size === 'lg' ? 'text-2xl' : 'text-lg',
        )}
      >
        {value}
      </div>
      {sub ? <div className="mt-1 text-[11px] leading-snug tabular-nums text-gray-500">{sub}</div> : null}
    </CardContent>
  </Card>
);

export const TileRow: React.FC<{ children: ReactNode; min?: string }> = ({ children, min = '190px' }) => (
  <div
    className="grid gap-3"
    style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))` }}
  >
    {children}
  </div>
);
